import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as PromptsAPI from 'api/Prompts';
import { unwrap, apiErrorMessage, notify } from 'utils/notify';

// ───────────────────────────────────────────────────────────────────────────
// Prompt-generator store — mirrors the singleton /api/super/prompt-generator/.
//
// One row, so there is no list/pagination state here: `data` is the row as the
// server last returned it, and the page keeps its own draft until Save.
// `loading` covers the GET, `saving` the PATCH, so a save spinner never blanks
// out the form.
// ───────────────────────────────────────────────────────────────────────────

// The two numeric columns must go over the wire as JSON numbers (the curl
// sends 0.3 / 8192, not "0.3" / "8192"), but MUI TextFields hand back strings.
// Empty string means "clear it" → send null rather than NaN.
const NUMERIC_KEYS = ['temperature', 'max_tokens'];

export const toApiPayload = (body) =>
  Object.fromEntries(
    Object.entries(body).map(([k, v]) => {
      if (!NUMERIC_KEYS.includes(k)) return [k, v];
      if (v === '' || v == null) return [k, null];
      const n = Number(v);
      return [k, Number.isNaN(n) ? v : n];
    })
  );

const initialState = {
  data: {},
  loading: false,
  saving: false,
  error: null
};

// ── thunks ────────────────────────────────────────────────────────────────

const getPromptGenerator = createAsyncThunk('prompts/getPromptGenerator', async (signal, { rejectWithValue }) => {
  try {
    const res = await PromptsAPI.getPromptGenerator(signal);
    return unwrap(res) || {};
  } catch (error) {
    // The page aborts its in-flight GET on unmount — that's not a failure the
    // user needs a snackbar about.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return rejectWithValue(null);
    const message = apiErrorMessage(error, 'Failed to load prompt configuration.');
    notify.error(message);
    return rejectWithValue(message);
  }
});

// `body` is the changed-keys subset — PATCH is partial, so anything left out
// (typically `api_key`) keeps its stored value.
const updatePromptGenerator = createAsyncThunk('prompts/updatePromptGenerator', async (body, { rejectWithValue }) => {
  try {
    const res = await PromptsAPI.updatePromptGenerator(toApiPayload(body));
    notify.success('Prompt configuration updated.');
    return unwrap(res) || {};
  } catch (error) {
    const message = apiErrorMessage(error);
    notify.error(message);
    return rejectWithValue(message);
  }
});

// ── slice ─────────────────────────────────────────────────────────────────

const PromptsSlice = createSlice({
  name: 'prompts',
  initialState,
  reducers: {
    resetState: () => initialState
  },
  extraReducers: {
    [getPromptGenerator.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [getPromptGenerator.fulfilled]: (state, action) => {
      state.loading = false;
      state.data = action.payload || {};
    },
    [getPromptGenerator.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error?.message;
      // Nothing configured yet (or the row was never created) — leave the page
      // with a blank form rather than a permanently empty error state.
      state.data = {};
    },

    [updatePromptGenerator.pending]: (state) => {
      state.saving = true;
      state.error = null;
    },
    [updatePromptGenerator.fulfilled]: (state, action) => {
      state.saving = false;
      // PATCH echoes the full row back; merge so a partial response can't drop
      // fields the form is still showing.
      state.data = { ...state.data, ...(action.payload || {}) };
    },
    [updatePromptGenerator.rejected]: (state, action) => {
      state.saving = false;
      state.error = action.payload || action.error?.message;
    }
  }
});

const { resetState } = PromptsSlice.actions;

export default PromptsSlice.reducer;

export { resetState, getPromptGenerator, updatePromptGenerator };
